// Notification Node Component

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import {
  Notifications as NotificationIcon,
  Email as EmailIcon,
  Chat as SlackIcon,
  Sms as SmsIcon,
  Campaign as BroadcastIcon,
  CheckCircle as SentIcon,
  Error as ErrorIcon,
  Send as SendingIcon
} from '@mui/icons-material';

interface NotificationNodeData {
  label: string;
  description?: string;
  channel?: 'email' | 'slack' | 'teams' | 'sms' | 'webhook' | 'push';
  status?: 'idle' | 'sending' | 'sent' | 'failed' | 'delivered';
  recipients?: string[];
  message?: string;
  deliveryCount?: number;
  failureCount?: number;
  lastSent?: string;
}

const NotificationNode: React.FC<NodeProps<NotificationNodeData>> = ({ 
  data, 
  selected, 
  id 
}) => {
  const getChannelIcon = () => {
    switch (data.channel) {
      case 'email':
        return <EmailIcon />;
      case 'slack':
        return <SlackIcon />;
      case 'teams':
        return <SlackIcon />; // Using same icon for Teams
      case 'sms':
        return <SmsIcon />;
      case 'webhook':
        return <BroadcastIcon />;
      case 'push':
        return <NotificationIcon />;
      default:
        return <NotificationIcon />;
    }
  };

  const getStatusIcon = () => {
    switch (data.status) {
      case 'sending':
        return <SendingIcon sx={{ color: '#2196F3', animation: 'pulse 1.5s ease-in-out infinite' }} />;
      case 'sent':
      case 'delivered':
        return <SentIcon sx={{ color: '#4CAF50' }} />;
      case 'failed':
        return <ErrorIcon sx={{ color: '#F44336' }} />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (data.status) {
      case 'sending':
        return '#E3F2FD';
      case 'sent':
      case 'delivered':
        return '#E8F5E9';
      case 'failed':
        return '#FFEBEE';
      default:
        return '#E8EAF6';
    }
  };

  const getBorderColor = () => {
    if (selected) return '#1976D2';
    switch (data.status) {
      case 'sending':
        return '#2196F3';
      case 'sent':
      case 'delivered':
        return '#4CAF50';
      case 'failed':
        return '#F44336';
      default:
        return '#673AB7';
    }
  };

  const getChannelColor = () => {
    switch (data.channel) {
      case 'email':
        return '#FF5722';
      case 'slack':
        return '#4A154B';
      case 'teams':
        return '#6264A7';
      case 'sms':
        return '#4CAF50';
      case 'webhook':
        return '#9C27B0';
      case 'push':
        return '#FF9800';
      default:
        return '#673AB7';
    }
  };

  const formatChannel = (channel?: string) => {
    if (!channel) return 'Notification';
    return channel.charAt(0).toUpperCase() + channel.slice(1);
  };

  const getSuccessRate = () => {
    const total = (data.deliveryCount || 0) + (data.failureCount || 0);
    if (total === 0) return 100;
    return Math.round(((data.deliveryCount || 0) / total) * 100);
  };

  return (
    <Box sx={{ minWidth: 200 }}>
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: getBorderColor(),
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%'
        }}
      />
      
      <Card
        sx={{
          backgroundColor: getStatusColor(),
          border: `2px solid ${getBorderColor()}`,
          borderRadius: 2,
          minHeight: 100,
          transition: 'all 0.2s ease',
          boxShadow: selected ? 6 : 2,
          position: 'relative',
          '&:hover': {
            boxShadow: 8,
            transform: 'translateY(-2px)'
          }
        }}
      >
        {/* Sending wave effect */}
        {data.status === 'sending' && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              right: -10,
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${getBorderColor()}40 0%, transparent 70%)`,
              animation: 'wave 1.5s ease-out infinite',
              pointerEvents: 'none'
            }}
          />
        )}

        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Header with Icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: getChannelColor(),
                mr: 1.5
              }}
            >
              {getChannelIcon()}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  lineHeight: 1.2
                }}
              >
                {data.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem'
                }}
              >
                {formatChannel(data.channel)}
              </Typography>
            </Box>
            <Box sx={{ ml: 1 }}>
              {getStatusIcon()}
            </Box>
          </Box>

          {/* Message preview */}
          {data.message && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 1.5,
                fontSize: '0.8rem',
                lineHeight: 1.3,
                fontStyle: 'italic',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              "{data.message}"
            </Typography>
          )}

          {/* Recipients info */}
          {data.recipients && data.recipients.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Chip
                label={`${data.recipients.length} recipient${data.recipients.length !== 1 ? 's' : ''}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: `${getChannelColor()}20`,
                  color: getChannelColor()
                }}
              />
            </Box>
          )}

          {/* Status and Stats */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Chip
              label={data.status || 'idle'}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: getBorderColor(),
                color: 'white',
                textTransform: 'uppercase'
              }}
            />
            
            {(data.deliveryCount || data.failureCount) && (
              <Chip
                label={`${getSuccessRate()}% success`}
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  borderColor: getBorderColor(),
                  color: getBorderColor()
                }}
              />
            )}
          </Box>

          {/* Delivery stats */}
          {(data.deliveryCount || data.failureCount) && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {data.deliveryCount && data.deliveryCount > 0 && (
                <Chip
                  label={`${data.deliveryCount} sent`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    backgroundColor: '#4CAF5020',
                    color: '#4CAF50'
                  }}
                />
              )}
              {data.failureCount && data.failureCount > 0 && (
                <Chip
                  label={`${data.failureCount} failed`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    backgroundColor: '#F4433620',
                    color: '#F44336'
                  }}
                />
              )}
            </Box>
          )}

          {/* Last sent info */}
          {data.lastSent && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem',
                mt: 1,
                display: 'block'
              }}
            >
              Last sent: {new Date(data.lastSent).toLocaleString()}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: getBorderColor(),
          width: 10,
          height: 10,
          border: '2px solid #fff',
          borderRadius: '50%'
        }}
      />

      {/* Sending indicator */}
      {data.status === 'sending' && (
        <Box
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: '#2196F3',
            animation: 'pulse 1s ease-in-out infinite',
            zIndex: 10
          }}
        />
      )}

      {/* Add animation keyframes */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.2);
              opacity: 0.7;
            }
          }
          
          @keyframes wave {
            0% {
              transform: scale(0);
              opacity: 1;
            }
            100% {
              transform: scale(2);
              opacity: 0;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default memo(NotificationNode);